/**
 * CapabilityRegistry — 内容工厂能力（MCP/Skill/SDK）统一事实源
 *
 * 单一数据源：tool_registry_list (seed.rs 的 content_factory 包: figma/crawler/...)
 * 工作流不再在此管理（已移至 WorkflowCenter / workflowStore），避免概念重叠。
 *
 * 导出统一的 ContentCapability 列表，供 OmniRegistryMatrix（能力典藏）消费。
 * 降级时（非 Tauri/后端空）保留内置默认（标注 builtin，非 mock）。
 */
import { omniInvoke } from '../../utils/bridge';
import { BridgeUtils } from '../../utils/bridge';

/** 内容工厂能力统一结构 (对齐 RegistryPackage 接口) */
export interface ContentCapability {
  id: string;
  type: string;        // MCP Node / Skill Tree / Native SDK / External API / Workflow Matrix
  name: string;
  status: string;      // active / standby
  desc: string;
  source: 'seed' | 'catalog' | 'builtin';
  grade?: string;      // MCP Node / Skill Tree / ... (用于 UI 分级标签)
  builtin?: boolean;
}

/** 内置默认能力（降级用，与 seed.rs 一致，标注 builtin 区分真实与示例） */
const BUILTIN_CAPABILITIES: ContentCapability[] = [
  { id: 'mcp:figma', type: 'MCP Node', name: 'Figma Architect', status: 'active', desc: 'studio.registry.figma', source: 'builtin', grade: 'MCP Node', builtin: true },
  { id: 'skill:anysearch-ai', type: 'MCP Node', name: 'AnySearch Unified', status: 'active', desc: 'studio.registry.anysearch', source: 'builtin', grade: 'MCP Node', builtin: true },
  { id: 'skill:web-crawler', type: 'Skill Tree', name: 'DeepWeb Crawler', status: 'standby', desc: 'studio.registry.crawler', source: 'builtin', grade: 'Skill Tree', builtin: true },
  { id: 'sdk:tauri-sys', type: 'Native SDK', name: 'Tauri FileSystem Core', status: 'active', desc: 'studio.registry.tauri', source: 'builtin', grade: 'Native SDK', builtin: true },
  { id: 'api:stripe-billing', type: 'External API', name: 'Stripe Zero-Trust', status: 'standby', desc: 'studio.registry.stripe', source: 'builtin', grade: 'External API', builtin: true },
  { id: '3d:spatial-seed', type: 'Workflow Matrix', name: 'Spatial Seed Injector', status: 'active', desc: 'content_factory.3d_director.spatial_seed', source: 'builtin', grade: 'Workflow Matrix', builtin: true },
];

/**
 * 加载内容工厂全部能力（双源合并 + 去重）
 *
 * @param page catalog 分页（技能 YAML 数量大）
 * @param limit 每页数量
 * @returns { capabilities, total, usedFallback }
 */
export async function loadContentCapabilities(
  page = 1,
  limit = 50,
): Promise<{ capabilities: ContentCapability[]; total: number; usedFallback: boolean }> {
  const capabilities: ContentCapability[] = [];
  let total = 0;
  let usedFallback = false;

  // 源 1: tool_registry seed 包 (content_factory tool_type)
  try {
    // [2026-10-05] 工具目录是"聚合多源"的一次读取：失败只少一路来源（后面还有兜底），
    // 不该由 bridge 拉起全屏崩溃罩。声明为后台读数。
    const tools = await omniInvoke<any[]>('tool_registry_list', undefined, { background: true });
    if (Array.isArray(tools)) {
      for (const tool of tools) {
        // 只取 content_factory 类型 (seed 里 metadata.tool_type === 'content_factory')
        const meta = tool.metadata || {};
        if (meta.tool_type !== 'content_factory' && tool.asset_domain !== 'content_factory') continue;
        capabilities.push({
          id: tool.id,
          type: meta.grade || 'Capability',
          name: tool.display_name,
          status: meta.registry_status || (tool.enabled ? 'active' : 'standby'),
          desc: meta.desc_i18n_key || tool.display_name,
          source: 'seed',
          grade: meta.grade,
        });
        total++;
      }
    }
  } catch {
    // tool_registry 不可用，继续尝试 catalog
  }

  // NOTE: 源 2 (director_fetch_store_catalogs) 已移除。The Sky 现在是纯能力
  // 管理（MCP/Skill/SDK），工作流统一在 WorkflowCenter 面板管理，避免同一份
  // 云端目录被两处消费、语义不同造成的概念混淆。

  // 降级：tool_registry 为空 → 用内置默认（标注 builtin）
  if (capabilities.length === 0) {
    capabilities.push(...BUILTIN_CAPABILITIES);
    total = capabilities.length;
    usedFallback = true;
  }

  return { capabilities, total, usedFallback };
}

/** 同步获取内置默认（用于 SSR 或即时渲染，不阻塞） */
export function getBuiltinCapabilities(): ContentCapability[] {
  return BUILTIN_CAPABILITIES.map(c => ({ ...c }));
}

// [2026-09-05] 原有一行 `export const isCapabilityBackendReady = BridgeUtils.isTauri;`
// 已删除。它是**顶层常量**，在模块初始化时就要读 `BridgeUtils` —— 而
// `bridge.ts` 与本模块存在循环导入，求值那一刻 `BridgeUtils` 是 `undefined`，
// 于是抛 `TypeError: Cannot read properties of undefined (reading 'isTauri')`，
// 被全局错误处理器接住后整页变成「FATAL KERNEL PANIC (UI LAYER)」。
//
// 也就是说 app.123all.ai / app.123xinzhi.com 打开就是一屏错误 —— 而这个导出
// **全仓库无人引用**，它除了让应用崩溃之外没有任何作用。
//
// 若将来真要这个标志，不要再写成顶层快照：跨模块的运行时标志必须惰性求值
// （函数或 getter），否则它的正确性取决于打包器的模块初始化顺序，
// 而那个顺序会随代码分块变化而变化 —— 今天能跑不代表明天能跑。
